/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { RootState } from "src/store";
import { connect } from "react-redux";
import { Stack, useTheme } from "@mui/material";
import { Chip } from "@itk/mui-components";
import { DekoxIcon } from "src/components";
import { filterActions } from "../../filter-store";
import { getAllFilters, TAppliedFilterChip } from "../../filter-types";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { useParams } from "react-router";
import { useRef } from "react";

const mapStateToProps = (state: RootState) => ({
  formData: state.pageReducer.combinedConfiguratorReducer.filterReducer.formData,
  appliedFilter: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter
});

const ownActions = {
  setFormData: filterActions.setFormData,
  setAppliedFilter: configuratorActions.setAppliedFilter,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const AppliedFiltersBase = (props: Props) => {
  const theme = useTheme();

  const { formData, appliedFilter, setFormData, setAppliedFilter, getConfigurationsRequest } = props;

  const { projectId = "" } = useParams<{ projectId: string }>();

  const timeoutId = useRef<ReturnType<typeof setTimeout>>();

  const removeById = <T extends { id?: string }>(items: T[] | undefined, id: string): T[] => items?.filter(x => x.id !== id) ?? [];

  const removeValueFromAttribute = <T extends { id?: string; values?: { id?: string }[] }>(attributes: T[] | undefined, attributeId: string, valueId: string): T[] =>
    attributes?.map(a => (a.id === attributeId ? { ...a, values: removeById(a.values, valueId) } : a)) ?? [];

  const handleDelete = (filterChip: TAppliedFilterChip) => {
    const isLabel = !filterChip.attributeId;

    const newApplied = isLabel
      ? { ...appliedFilter, labels: removeById(appliedFilter.labels, filterChip.valueId) }
      : { ...appliedFilter, attributes: removeValueFromAttribute(appliedFilter.attributes, filterChip.attributeId!, filterChip.valueId) };

    const newFormData = isLabel
      ? { ...formData, labels: removeById(formData.labels, filterChip.valueId) }
      : { ...formData, attributes: removeValueFromAttribute(formData.attributes, filterChip.attributeId!, filterChip.valueId) };

    setFormData(newFormData);
    setAppliedFilter(newApplied);
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => getConfigurationsRequest(projectId, newApplied), 2000);
  };

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {Object.values(appliedFilter).some(x => x.length > 0) &&
        getAllFilters(appliedFilter).map(filterChip => {
          return (
            <Chip
              key={`${filterChip?.attributeId}-${filterChip.valueId}`}
              label={filterChip?.attributeName ? `${filterChip.attributeName} - ${filterChip.valueDescription}` : filterChip.valueDescription}
              size="small"
              sx={{ borderRadius: 1, bgcolor: theme.palette.customColors.white, border: `1px solid ${theme.palette.customColors.grey2}` }}
              deleteIcon={<DekoxIcon icon="close" />}
              onDelete={() => handleDelete(filterChip)}
            />
          );
        })}
    </Stack>
  );
};

export const AppliedFilters = connect(mapStateToProps, ownActions)(AppliedFiltersBase);
